const MAX_REDIRECT_ATTEMPTS = 3;
const LOGIN_IN_FLIGHT_TTL_MS = 20000;
const NON_AUTH_RETRY_BASE_DELAY_MS = 1500;
const NON_AUTH_RETRY_MAX_DELAY_MS = 10000;

type LoginStartDecision =
  | {
      allowed: false;
      reason: "in_flight" | "max_attempts";
      attempts?: number;
      elapsedMs?: number;
      ttlMs?: number;
    }
  | {
      allowed: true;
    };

type AuthFlowSnapshot = {
  handlingAuthCallback: boolean;
  lastHandledAuthCallbackUrl: string;
  loginInFlight: boolean;
  loginStartedAt: number;
  nextAutoRedirectDelayMs: number | null;
  nonAuthRetryAttempts: number;
  redirectAttempts: number;
};

type ReauthRequestSource =
  | "auth0_protected_app_error"
  | "urql_bootstrap"
  | "urql_refresh_auth"
  | "user_provider_fetch_timeout"
  | "user_provider_missing_auth0_user"
  | "user_provider_user_query_error";

type ReauthRequest = {
  id: number;
  requestedAt: number;
  source: ReauthRequestSource;
};

type ReauthRequestListener = (request: ReauthRequest) => void;

class AuthFlowController {
  private state: AuthFlowSnapshot = {
    handlingAuthCallback: false,
    lastHandledAuthCallbackUrl: "",
    loginInFlight: false,
    loginStartedAt: 0,
    nextAutoRedirectDelayMs: null,
    nonAuthRetryAttempts: 0,
    redirectAttempts: 0,
  };
  private lastReauthRequest: ReauthRequest | null = null;
  private lastReauthRequestId = 0;
  private lastHandledReauthRequestId = 0;
  private reauthRequestListeners = new Set<ReauthRequestListener>();

  getSnapshot(): AuthFlowSnapshot {
    return { ...this.state };
  }

  resetAfterAuthenticated(): void {
    this.state.redirectAttempts = 0;
    this.state.loginInFlight = false;
    this.state.handlingAuthCallback = false;
    this.state.lastHandledAuthCallbackUrl = "";
    this.state.nonAuthRetryAttempts = 0;
    this.state.nextAutoRedirectDelayMs = null;
  }

  decideLoginStart(now: number): LoginStartDecision {
    if (this.state.redirectAttempts >= MAX_REDIRECT_ATTEMPTS) {
      return {
        allowed: false,
        attempts: this.state.redirectAttempts,
        reason: "max_attempts",
      };
    }

    if (
      this.state.loginInFlight &&
      now - this.state.loginStartedAt < LOGIN_IN_FLIGHT_TTL_MS
    ) {
      return {
        allowed: false,
        elapsedMs: now - this.state.loginStartedAt,
        reason: "in_flight",
        ttlMs: LOGIN_IN_FLIGHT_TTL_MS,
      };
    }

    return { allowed: true };
  }

  markLoginStarted(now: number): void {
    this.state.loginInFlight = true;
    this.state.loginStartedAt = now;
  }

  markLoginFinished(): void {
    this.state.loginInFlight = false;
  }

  resetForManualRetry(): void {
    this.state.redirectAttempts = 0;
    this.state.loginInFlight = false;
    this.state.loginStartedAt = 0;
    this.state.nonAuthRetryAttempts = 0;
    this.state.nextAutoRedirectDelayMs = null;
  }

  hasReachedMaxRedirectAttempts(): boolean {
    return this.state.redirectAttempts >= MAX_REDIRECT_ATTEMPTS;
  }

  incrementRedirectAttempts(): number {
    this.state.redirectAttempts += 1;
    return this.state.redirectAttempts;
  }

  getCurrentRedirectAttempts(): number {
    return this.state.redirectAttempts;
  }

  getNextAutoRedirectDelay(defaultDelayMs: number): number {
    return this.state.nextAutoRedirectDelayMs ?? defaultDelayMs;
  }

  clearNextAutoRedirectDelay(): void {
    this.state.nextAutoRedirectDelayMs = null;
  }

  startAuthCallback(url: string): boolean {
    if (this.state.handlingAuthCallback) {
      return false;
    }

    if (this.state.lastHandledAuthCallbackUrl === url) {
      return false;
    }

    this.state.handlingAuthCallback = true;
    this.state.lastHandledAuthCallbackUrl = url;
    return true;
  }

  finishAuthCallback(): void {
    this.state.handlingAuthCallback = false;
    this.state.loginInFlight = false;
    this.state.nonAuthRetryAttempts = 0;
    this.state.nextAutoRedirectDelayMs = null;
  }

  markFatalConfigError(): void {
    this.state.redirectAttempts = MAX_REDIRECT_ATTEMPTS;
    this.state.loginInFlight = false;
    this.state.loginStartedAt = 0;
    this.state.nextAutoRedirectDelayMs = null;
  }

  resetInFlightLogin(): void {
    this.state.loginInFlight = false;
    this.state.loginStartedAt = 0;
  }

  scheduleNonAuthRetry(): {
    backoffDelay: number;
    nonAuthRetryAttempts: number;
  } {
    this.state.nonAuthRetryAttempts += 1;
    const backoffDelay = Math.min(
      NON_AUTH_RETRY_BASE_DELAY_MS * 2 ** (this.state.nonAuthRetryAttempts - 1),
      NON_AUTH_RETRY_MAX_DELAY_MS
    );
    this.state.nextAutoRedirectDelayMs = backoffDelay;
    return {
      backoffDelay,
      nonAuthRetryAttempts: this.state.nonAuthRetryAttempts,
    };
  }

  requestReauth(source: ReauthRequestSource): ReauthRequest {
    this.lastReauthRequestId += 1;
    const request = {
      id: this.lastReauthRequestId,
      requestedAt: Date.now(),
      source,
    };
    this.lastReauthRequest = request;
    this.reauthRequestListeners.forEach((listener) => listener(request));
    return request;
  }

  getLastReauthRequest(): ReauthRequest | null {
    return this.lastReauthRequest;
  }

  shouldHandleReauthRequest(request: ReauthRequest): boolean {
    return request.id > this.lastHandledReauthRequestId;
  }

  markReauthRequestHandled(requestId: number): void {
    this.lastHandledReauthRequestId = Math.max(
      this.lastHandledReauthRequestId,
      requestId
    );
  }

  subscribeToReauthRequests(listener: ReauthRequestListener): () => void {
    this.reauthRequestListeners.add(listener);
    return () => {
      this.reauthRequestListeners.delete(listener);
    };
  }
}

const authFlowController = new AuthFlowController();

export { authFlowController };
export type { ReauthRequest, ReauthRequestSource };
